/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import {
  $ApiTokenFilters,
  ApiToken,
  ApiTokenFilters,
  ApiTokenSortableFields,
  GetApiTokensData,
  GetApiTokensResponse,
} from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { jsonSchemaToTypeMap } from '@/utils/filters';

export type UsePaginatedAPITokensQueryParams = {
  params?: Omit<GetApiTokensData, 'tenantId'>;
  type?: 'inMemory' | 'queryParams';
};

/**
 * Custom hook that fetches paginated API tokens with filtering, sorting and real-time updates.
 * Combines React Query for data fetching with WebSocket subscriptions for real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated apiTokens response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
export function usePaginatedAPITokensQuery({ params, type }: UsePaginatedAPITokensQueryParams) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  return usePaginatedQuery<GetApiTokensResponse, ApiToken, ApiTokenFilters, ApiTokenSortableFields, GetApiTokensData>({
    apiCall: mgmtPlaneAPIClient.apiTokens.getApiTokens.bind(mgmtPlaneAPIClient.apiTokens),
    filterTypes: jsonSchemaToTypeMap($ApiTokenFilters),
    queryKey: 'apiTokens',
    realtimeEvents: ['api-token:created', 'api-token:revoke'],
    params: {
      tenantId: selectedTenantId!,
      ...params,
    },
    type,
    enabled: !!selectedTenantId,
  });
}
